import { useCallback, useEffect, useRef } from 'react';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select';
import { Skeleton } from '~/components/ui/skeleton';
import {
  BaseChatPanel,
  BaseChatPanelHeader,
} from '~/features/chat/components/base-chat-panel/base-chat-panel';
import { ChatInput } from '~/features/chat/components/chat-panel/chat-input';
import { cn } from '~/utils/cn';

import { useExperimentActions } from '../../hooks/use-experiment-actions';
import { useExperimentChatbotMessages } from '../../hooks/use-experiment-chatbot-messages';
import { useExperimentChatbotQa } from '../../hooks/use-experiment-chatbot-qa';
import { ALL_TIMELINE_STAGES } from '../../services/build-timeline-narration';
import { useExperimentChatbotStore } from '../../store/experiment-chatbot.store';
import type { AnalyticsSensitivity } from '../../types/analytics-controls.types';
import type { ChatbotView, TimelineStage } from '../../types/chatbot-message.types';

import { ChatbotMessageRenderer } from './chatbot-message-renderer';
import { QuickActionChipsRow, type QuickActionChip } from './quick-action-chips-row';

/** Canned analytical prompts for category-mode Performance — clicking routes the label through the live Q&A loop. */
const CATEGORY_QUICK_ACTION_CHIPS: QuickActionChip[] = [
  { id: 'pajama-set-decline', label: "What's driving Women's Pajama Set decline?" },
  { id: 'expand-or-cut', label: 'Which categories should I expand or cut?' },
];

interface ExperimentChatbotPanelProps {
  experimentId: string;
  /**
   * Active chatbot rendering mode — mirrors the active main tab + Details
   * sub-tab on the Experiment Details page. `timeline` shows the day-by-day
   * narration; `performance` / `health` show analytical summaries.
   */
  view: ChatbotView;
  /**
   * Fixed outlier sensitivity level. Threaded into the analytics
   * agent-summary fetch so the chatbot intro reflects the app's default
   * threshold.
   */
  sensitivity: AnalyticsSensitivity;
  /** Optional className for the outer container; default styling assumes the panel sits as a left column. */
  className?: string | undefined;
}

/**
 * Lily Agent panel for the Experiment Details page. Scripted narration is
 * driven by `useExperimentChatbotMessages` (real data with mock fallback for
 * fields the backend hasn't exposed yet); action buttons fire
 * `useExperimentActions` toast stubs. The dev stage selector (dev-only,
 * tree-shaken in prod) is an override knob — pick `(auto · derived)` to use
 * the live experiment state. Phase 5: the input streams free-form analytics
 * Q&A through the ai-mcp-server via `useExperimentChatbotQa`, scoped to this
 * experiment.
 */
export function ExperimentChatbotPanel({
  experimentId,
  view,
  sensitivity,
  className,
}: ExperimentChatbotPanelProps) {
  const devOverride = useExperimentChatbotStore((s) => s.devStageOverride);
  const setDevOverride = useExperimentChatbotStore((s) => s.setDevStageOverride);

  const { messages, isLoading, isError } = useExperimentChatbotMessages(experimentId, view, {
    sensitivity,
  });
  const { handleAction, dialog: terminalActionDialog } = useExperimentActions(experimentId);
  const {
    qaMessages,
    isStreaming: isQaStreaming,
    sendMessage,
    abortStream,
    retry,
  } = useExperimentChatbotQa(experimentId);

  // Quick-action chips appear above the input only on the Category view
  // (matches screenshot 17); clicking sends the chip's prompt through the same
  // live Q&A loop as typed input.
  const showQuickActionChips = view === 'category';
  const handleChipClick = useCallback(
    (chip: QuickActionChip) => {
      sendMessage(chip.label);
    },
    [sendMessage],
  );

  // Keep the newest Q&A content in view: always jump on a new turn (the user
  // just sent it), but during token streaming only follow when they're already
  // near the bottom — never disturbs the scripted narration's scroll position.
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const prevQaCountRef = useRef(0);
  const lastQa = qaMessages[qaMessages.length - 1];
  const qaCount = qaMessages.length;
  const qaTextLength = lastQa?.kind === 'assistant-text' ? lastQa.text.length : 0;
  // The trailing turn settling into a terminal card (error / empty) doesn't
  // change qaCount or qaTextLength (the streaming placeholder was already
  // counted), so track its kind to still scroll the fresh card into view.
  const lastQaKind = lastQa?.kind;
  useEffect(() => {
    const isNewTurn = qaCount > prevQaCountRef.current;
    prevQaCountRef.current = qaCount;
    if (qaCount === 0) return;
    const container = scrollContainerRef.current;
    if (!container) return;
    const nearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 120;
    if (isNewTurn || nearBottom) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [qaCount, qaTextLength, lastQaKind]);

  return (
    <BaseChatPanel
      className={cn('bg-background border-r', className)}
      scrollContainerRef={scrollContainerRef}
      header={
        <BaseChatPanelHeader
          title="Lily Agent"
          actions={
            view === 'timeline' && import.meta.env.DEV ? (
              <DevStageSelector value={devOverride} onChange={setDevOverride} />
            ) : null
          }
        />
      }
      belowMessages={
        showQuickActionChips ? (
          <QuickActionChipsRow chips={CATEGORY_QUICK_ACTION_CHIPS} onChipClick={handleChipClick} />
        ) : null
      }
      input={
        <ChatInput
          variant="compact"
          placeholder="Ask about this experiment…"
          showAttachButton={false}
          onSend={sendMessage}
          onAbort={abortStream}
          isStreaming={isQaStreaming}
        />
      }
    >
      {isError ? (
        <ChatbotErrorState />
      ) : isLoading && messages.length === 0 ? (
        <ChatbotLoadingSkeleton />
      ) : (
        messages.map((msg) => (
          <ChatbotMessageRenderer key={msg.id} message={msg} onAction={handleAction} />
        ))
      )}
      {/* Live Q&A turns render after the scripted narration, closest to the
          input. Failed turns render an inline error card (with a Retry button
          when retryable) in place of the assistant bubble — one per failure. */}
      {qaMessages.map((msg) => (
        <ChatbotMessageRenderer
          key={msg.id}
          message={msg}
          onAction={handleAction}
          onRetry={retry}
        />
      ))}
      {/* Sentinel for the Q&A auto-scroll effect above. */}
      <div ref={bottomRef} aria-hidden="true" />
      {/* Mounted here so it can portal above the panel; controlled by useExperimentActions. */}
      {terminalActionDialog}
    </BaseChatPanel>
  );
}

const AUTO_OPTION = '__auto__';

/**
 * Dev / QA stage selector pinned to the header. The first option ("auto") feeds
 * `null` into the store so the panel uses real experiment derivation; the rest
 * pin to a specific checkpoint for screenshot review. Render-site is guarded by
 * `import.meta.env.DEV` so this never ships to prod.
 */
function DevStageSelector({
  value,
  onChange,
}: {
  value: TimelineStage | null;
  onChange: (next: TimelineStage | null) => void;
}) {
  return (
    <Select
      value={value ?? AUTO_OPTION}
      onValueChange={(next) => {
        onChange(next === AUTO_OPTION ? null : (next as TimelineStage));
      }}
    >
      <SelectTrigger
        className="h-7 w-auto max-w-[180px] min-w-[120px] px-2 text-[11px] sm:max-w-[220px]"
        aria-label="Dev: select timeline stage"
      >
        <SelectValue placeholder="(auto · derived)" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={AUTO_OPTION}>(auto · derived)</SelectItem>
        {ALL_TIMELINE_STAGES.map((s) => (
          <SelectItem key={s.value} value={s.value}>
            {s.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/** Three placeholder cards rendered while the underlying queries resolve for the first time. */
function ChatbotLoadingSkeleton() {
  return (
    <>
      <Skeleton className="h-5 w-40 self-center rounded-full" />
      <Skeleton className="h-24 w-full rounded-xl" />
      <Skeleton className="h-16 w-full rounded-xl" />
      <Skeleton className="h-24 w-full rounded-xl" />
    </>
  );
}

/** Surfaced when the experiment fetch errors — the right pane renders `ExperimentLoadError` (subsidiary-aware recovery, or a not-found / load message), so the chatbot complements it with a retry hint. */
function ChatbotErrorState() {
  return (
    <div className="border-error/30 bg-error-50 space-y-1 rounded-xl border p-3">
      <p className="text-foreground text-sm font-medium">Couldn&apos;t load this experiment.</p>
      <p className="text-muted-foreground text-xs leading-5">
        Lily can&apos;t narrate without the experiment data. Refresh the page to try again.
      </p>
    </div>
  );
}
